import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { PaymentRuleDTO, PaymentRuleFormValues } from '../../types';
import { useAgentPaymentRuleAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const ATTRIBUTE_KEYS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
type AttributeKey = `Attribute${(typeof ATTRIBUTE_KEYS)[number]}`;

const EMPTY_FORM: PaymentRuleFormValues = {
  PaymentAccountID: 0,
  EventID: null,
  RequestID: null,
  ConversationID: null,
  AgentRunID: null,
  IsValidateSenderSignature: false,
  IsValidateRecipientSignature: false,
  Attribute1: '',
  Attribute2: '',
  Attribute3: '',
  Attribute4: '',
  Attribute5: '',
  Attribute6: '',
  Attribute7: '',
  Attribute8: '',
  Attribute9: '',
  Amount: 0,
  Currency: '',
  MaxSpendLimit: null,
  Hash: '',
};

/**
 * Restored Payment Rules tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_PaymentRules) confirmed by reading PaymentRule.cs directly: a blockchain
 * payment-validation record (PaymentAccountID/Amount/Currency/MaxSpendLimit/
 * IsValidateSenderSignature/IsValidateRecipientSignature/Attribute1-9), not a percentage-based
 * rule — exactly as briefed. Optional EventID/RequestID/ConversationID/AgentRunID linkage FKs and
 * an optional Hash column are real but not mentioned by the briefing — see types.ts's
 * PaymentRuleDTO comment. CONFIRMED BACKEND GAP (re-verified this pass): no by-agent route exists
 * on BasePaymentRuleController.cs — this list is loaded via the generic `list` endpoint filtered
 * client-side by AgentID; see agentPaymentRuleAdminApiClient.ts's header comment for the
 * fast-follow candidate this implies.
 */
export function PaymentRulesPanel({ agentId }: { agentId: number }) {
  const api = useAgentPaymentRuleAdminApiClient();
  const [rules, setRules] = useState<PaymentRuleDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<PaymentRuleDTO | 'new' | null>(null);
  const [form, setForm] = useState<PaymentRuleFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PaymentRuleDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getPaymentRulesByAgent(agentId);
    setRules(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (rule: PaymentRuleDTO | 'new') => {
    setEditing(rule);
    if (rule === 'new') {
      setForm(EMPTY_FORM);
      return;
    }
    const next: PaymentRuleFormValues = {
      PaymentAccountID: rule.PaymentAccountID,
      EventID: rule.EventID ?? null,
      RequestID: rule.RequestID ?? null,
      ConversationID: rule.ConversationID ?? null,
      AgentRunID: rule.AgentRunID ?? null,
      IsValidateSenderSignature: rule.IsValidateSenderSignature,
      IsValidateRecipientSignature: rule.IsValidateRecipientSignature,
      Amount: rule.Amount,
      Currency: rule.Currency,
      MaxSpendLimit: rule.MaxSpendLimit ?? null,
      Hash: rule.Hash ?? '',
    };
    for (const n of ATTRIBUTE_KEYS) {
      const key = `Attribute${n}` as AttributeKey;
      next[key] = rule[key] ?? '';
    }
    setForm(next);
  };

  const handleSave = async () => {
    if (!form.PaymentAccountID || !form.Amount || !form.Currency.trim()) return;
    setSaving(true);
    const result = editing === 'new' ? await api.createPaymentRule(agentId, form) : await api.updatePaymentRule((editing as PaymentRuleDTO).PaymentRuleID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deletePaymentRule(pendingDelete.PaymentRuleID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setRules((prev) => prev.filter((r) => r.PaymentRuleID !== pendingDelete.PaymentRuleID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Payment Rules" />
        <div className="aaa-form-group">
          <label>Payment Account ID *</label>
          <input
            type="number"
            value={form.PaymentAccountID || ''}
            onChange={(e) => setForm((f) => ({ ...f, PaymentAccountID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Amount *</label>
          <input
            type="number"
            step={0.01}
            value={form.Amount || ''}
            onChange={(e) => setForm((f) => ({ ...f, Amount: e.target.value ? parseFloat(e.target.value) : 0 }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Currency *</label>
          <input type="text" maxLength={10} value={form.Currency} onChange={(e) => setForm((f) => ({ ...f, Currency: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label>Max Spend Limit</label>
          <input
            type="number"
            step={0.01}
            value={form.MaxSpendLimit ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, MaxSpendLimit: e.target.value ? parseFloat(e.target.value) : null }))}
          />
        </div>

        <fieldset className="aaa-form-group-full aaa-fieldset">
          <legend>Linked Records (optional)</legend>
          <div className="aaa-form-grid">
            <div className="aaa-form-group">
              <label>Event ID</label>
              <input
                type="number"
                value={form.EventID ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, EventID: e.target.value ? parseInt(e.target.value, 10) : null }))}
              />
            </div>
            <div className="aaa-form-group">
              <label>Request ID</label>
              <input
                type="number"
                value={form.RequestID ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, RequestID: e.target.value ? parseInt(e.target.value, 10) : null }))}
              />
            </div>
            <div className="aaa-form-group">
              <label>Conversation ID</label>
              <input
                type="number"
                value={form.ConversationID ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, ConversationID: e.target.value ? parseInt(e.target.value, 10) : null }))}
              />
            </div>
            <div className="aaa-form-group">
              <label>Agent Run ID</label>
              <input
                type="number"
                value={form.AgentRunID ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, AgentRunID: e.target.value ? parseInt(e.target.value, 10) : null }))}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="aaa-form-group-full aaa-fieldset">
          <legend>Signature Validation</legend>
          <div className="aaa-checkbox-grid">
            <label className="aaa-checkbox">
              <input
                type="checkbox"
                checked={form.IsValidateSenderSignature}
                onChange={(e) => setForm((f) => ({ ...f, IsValidateSenderSignature: e.target.checked }))}
              />
              Validate Sender Signature
            </label>
            <label className="aaa-checkbox">
              <input
                type="checkbox"
                checked={form.IsValidateRecipientSignature}
                onChange={(e) => setForm((f) => ({ ...f, IsValidateRecipientSignature: e.target.checked }))}
              />
              Validate Recipient Signature
            </label>
          </div>
        </fieldset>

        <fieldset className="aaa-form-group-full aaa-fieldset">
          <legend>Attributes (optional)</legend>
          <div className="aaa-form-grid">
            {ATTRIBUTE_KEYS.map((n) => {
              const key = `Attribute${n}` as AttributeKey;
              return (
                <div className="aaa-form-group" key={key}>
                  <label>Attribute {n}</label>
                  <input type="text" value={form[key] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                </div>
              );
            })}
          </div>
        </fieldset>

        <div className="aaa-form-group aaa-form-group-full">
          <label>Hash</label>
          <input type="text" maxLength={200} value={form.Hash ?? ''} onChange={(e) => setForm((f) => ({ ...f, Hash: e.target.value }))} />
        </div>

        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Payment Rule'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Payment Rules" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Payment Rule
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && rules.length === 0 && <div className="aaa-entity-dropdown-empty">No payment rules yet.</div>}
      <div className="aaa-entity-grid">
        {rules.map((rule) => (
          <div key={rule.PaymentRuleID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>
                {rule.Amount} {rule.Currency}
              </strong>
            </div>
            <p>Payment Account #{rule.PaymentAccountID}{rule.MaxSpendLimit ? ` · Max spend ${rule.MaxSpendLimit}` : ''}</p>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(rule)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(rule)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Payment Rule"
        message={`Delete payment rule "${pendingDelete?.Amount} ${pendingDelete?.Currency}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
